import InputField from "components/Common/InputField"
import ThemeModal from "components/Common/ThemeModal"
import { useMemo, useCallback } from "react"
import { toast } from "react-toastify"
import { Form, FormFeedback, Label, Button } from "reactstrap"
import * as Yup from "yup"
import { useFormik } from "formik"
import {
  useCreateAccessoriesCompany,
  useEditAccessoriesCompany,
} from "hooks/useAccessoriesCompany"

const AccessoriesCompanyAddEdit = ({ modal, toggleModal, isEdit, editedData }) => {
  const { mutateAsync: createMutation, isPending: createLoading } =
    useCreateAccessoriesCompany()
  const { mutateAsync: editMutation, isPending: editLoading } =
    useEditAccessoriesCompany()

  // -------------------- VALIDATION --------------------
  const validationSchema = Yup.object({
    name: Yup.string()
      .required("Please enter store name")
      .max(255, "Name cannot exceed 255 characters"),
    description: Yup.string().max(1000, "Description cannot exceed 1000 characters"),
  })

  // -------------------- SUBMIT --------------------
  const onSubmit = useCallback(
    async (values, { resetForm }) => {
      try {
        const action = isEdit
          ? editMutation({ id: editedData.id, ...values })
          : createMutation(values)

        const res = await action

        toast.success(
          res?.message ??
            `Accessories Store ${isEdit ? "updated" : "created"} successfully.`
        )

        resetForm()
        toggleModal()
      } catch (error) {
        console.error(error)
        toast.error("Failed to save accessories store.")
      }
    },
    [isEdit, editedData, editMutation, createMutation, toggleModal]
  )

  // -------------------- FORMIK --------------------
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: editedData?.name || "",
      description: editedData?.description || "",
    },
    validationSchema,
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit,
  })

  // -------------------- UI STATES --------------------
  const buttonText = useMemo(() => {
    const loading = editLoading || createLoading
    return `${isEdit ? "Save Changes" : "Add Store"}${loading ? "..." : ""}`
  }, [isEdit, editLoading, createLoading])

  const isSubmitDisabled = editLoading || createLoading

  // -------------------- RENDER --------------------
  return (
    <ThemeModal
      isOpen={modal}
      toggle={toggleModal}
      title={isEdit ? "Edit Accessories Store" : "Add Accessories Store"}
    >
      <Form
        onSubmit={e => {
          e.preventDefault()
          formik.handleSubmit()
        }}
      >
        {/* Name */}
        <div className="mb-3">
          <Label htmlFor="name">
            Name <span className="text-danger">*</span>
          </Label>
          <InputField
            type="text"
            name="name"
            id="name"
            value={formik.values.name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            invalid={formik.touched.name && !!formik.errors.name}
          />
          {formik.touched.name && formik.errors.name && (
            <FormFeedback type="invalid">{formik.errors.name}</FormFeedback>
          )}
        </div>

        {/* Description */}
        <div className="mb-3">
          <Label htmlFor="description">Description</Label>
          <InputField
            type="textarea"
            name="description"
            id="description"
            value={formik.values.description}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            invalid={formik.touched.description && !!formik.errors.description}
          />
          {formik.touched.description && formik.errors.description && (
            <FormFeedback type="invalid">{formik.errors.description}</FormFeedback>
          )}
        </div>

        {/* Submit */}
        <div className="text-end mt-3">
          <Button color="success" type="submit" disabled={isSubmitDisabled}>
            {buttonText}
          </Button>
        </div>
      </Form>
    </ThemeModal>
  )
}

export default AccessoriesCompanyAddEdit
